import { Button } from "@/atoms"
import { NO_VALUE_PLACEHOLDER } from "@/const/default-values"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { preventDefaultWrapper } from "@/helpers/utils"
import { ShowMore } from "@/molecules/ShowMore"
import { ViolatedCellWithoutTooltip } from "../ViolatedCell"

import styles from "./ViolationsAiPolicyCell.module.scss"

import type { TFunction } from "@/hooks/useTranslation"
import type { Record } from "@/pages/GovernancePage/applications/policies/getDemoData.types"

const LABELS = "governance.ai.live.violations"

// Whether a real policy was violated within the list's window, as the demo's
// cell says it; hovering it details how often and when last, as the Last Scan
// column does, and a violated policy opens its violation log
export const ViolationsAiPolicyCell = (
  t: TFunction,
  windowDays: number,
  openViolations: (policyId: string) => void
) =>
  function ViolationsAiPolicyCellComponent(_: unknown, record: Record) {
    if (record.type === "heading" || !record.live) return null

    const { violated = false, violations = 0, lastViolationAt } = record.live
    const status = ViolatedCellWithoutTooltip(violated)

    return (
      <ShowMore
        id={`violations-ai-policy-cell-${record.key}`}
        label={
          <ShowMore.Label.Component>
            {violated ? (
              <Button
                type="link"
                size="small"
                aria-label={t(`${LABELS}.open`)}
                className={styles.open}
                // The row opens the policy; its violation status opens its
                // violations
                onClick={preventDefaultWrapper(() =>
                  openViolations(`${record.id}`)
                )}
              >
                {status}
              </Button>
            ) : (
              status
            )}
          </ShowMore.Label.Component>
        }
      >
        <ShowMore.Component title={t(`${LABELS}.title`)}>
          <ShowMore.Description
            items={[
              {
                label: t(`${LABELS}.count`, { count: windowDays }),
                value: `${violations}`
              },
              {
                label: t(`${LABELS}.last`),
                value: lastViolationAt
                  ? getDateInUserLocale(
                      getDateFromSeconds(lastViolationAt),
                      DEFAULT_LONG_FORMAT
                    )
                  : NO_VALUE_PLACEHOLDER
              }
            ]}
          />
        </ShowMore.Component>
      </ShowMore>
    )
  }
